import axios from "axios";
import { Toast } from "vant";
import router from "@/router";

// 在非构建环境(直接用浏览器打开)时，process 可能不存在，这里做兼容
const baseURL =
  typeof process !== "undefined" && process.env && process.env.VUE_APP_BASE_API
    ? process.env.VUE_APP_BASE_API
    : "/api";

const request = axios.create({
  baseURL,
  timeout: 10000,
});

/** 无需携带登录态的接口（避免本地过期 token 导致换 code 失败） */
const PUBLIC_PATHS = [
  "/mp/login/doLogin",
];
function isPublicAuthPath(url) {
  if (!url || typeof url !== "string") return false;
  const path = url.replace(/^https?:\/\/[^/]+/i, "").split("?")[0];
  return PUBLIC_PATHS.some((p) => path.includes(p));
}

/** 是否与后端约定为「成功」的业务码：status === 1 */
function isBusinessSuccess(res) {
  if (res == null || typeof res !== "object") return false;
  return res.status === 1;
}

/** 后端以业务码表示的登录态失效（status 非 1 且携带特定标识） */
const AUTH_FAIL_KEYWORDS = ["token", "登录", "未授权", "过期", "认证"];
function isBusinessAuthFailure(res) {
  if (res == null || typeof res !== "object") return false;
  const code = res.code ?? res.errorCode;
  if (code === 401 || code === -401 || code === 10401) return true;
  const msg = (res.errorMessage || res.message || "").toLowerCase();
  return AUTH_FAIL_KEYWORDS.some((kw) => msg.includes(kw));
}

function getBusinessErrorMessage(res) {
  if (res == null || typeof res !== "object") return "请求失败";
  return res.errorMessage || res.message || "请求失败";
}

/**
 * 提取 HTTP 非 2xx 响应中的后端业务提示。
 * Spring 业务异常返回 Result，参数/请求体异常也可能直接返回纯文本。
 */
function getHttpErrorMessage(data, fallback = "网络异常") {
  if (typeof data === "string" && data.trim()) return data.trim();
  if (data && typeof data === "object") {
    return (
      data.errorMessage ||
      data.message ||
      data.msg ||
      (typeof data.error === "string" ? data.error : "") ||
      fallback
    );
  }
  return fallback;
}

/** 保留响应信息，同时让页面 catch(e).message 拿到后端提示而不是 Axios 的 code 400。 */
function rejectWithMessage(error, message) {
  const normalizedError = new Error(message);
  normalizedError.response = error.response;
  normalizedError.code = error.code;
  return Promise.reject(normalizedError);
}

// 请求拦截器
request.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    const reqUrl = config.url || "";
    if (token && !isPublicAuthPath(reqUrl)) {
      config.headers.Authorization = token;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// 响应拦截器
request.interceptors.response.use(
  (response) => {
    const res = response.data;

    if (!isBusinessSuccess(res)) {
      if (isBusinessAuthFailure(res)) {
        Toast.fail("登录已过期，请重新登录");
        localStorage.removeItem("token");
        localStorage.removeItem("userInfo");
        sessionStorage.removeItem("entryFlowDone");
        router.replace({
          path: "/auth",
          query: { redirect: router.currentRoute.fullPath },
        });
        return Promise.reject(new Error("未授权"));
      }
      const errMsg = getBusinessErrorMessage(res);
      Toast.fail(errMsg);
      return Promise.reject(new Error(errMsg));
    }

    if (res.token) {
      localStorage.setItem("token", res.token);
    }

    return res.data !== undefined ? res.data : res;
  },
  (error) => {
    // 处理 HTTP 错误状态码
    if (error.response) {
      const { status, data } = error.response;

      // 401: 未授权，token 过期或无效
      if (status === 401) {
        Toast.fail("登录已过期，请重新登录");
        // 清除本地存储
        localStorage.removeItem("token");
        localStorage.removeItem("userInfo");
        // 跳转到登录页
        router.replace({
          path: "/auth",
          query: { redirect: router.currentRoute.fullPath },
        });
        return Promise.reject(new Error("未授权"));
      }

      // 403: 禁止访问
      if (status === 403) {
        Toast.fail("没有权限访问");
        return Promise.reject(new Error("禁止访问"));
      }

      // 404: 资源不存在
      if (status === 404) {
        Toast.fail("请求的资源不存在");
        return Promise.reject(new Error("资源不存在"));
      }

      // 500: 服务器错误
      if (status >= 500) {
        Toast.fail("服务器错误，请稍后重试");
        return Promise.reject(new Error("服务器错误"));
      }

      // 其他错误
      const msg = getHttpErrorMessage(data, error.message || "网络异常");
      Toast.fail(msg);
      return rejectWithMessage(error, msg);
    }

    // 网络错误或超时
    if (error.message.includes("timeout")) {
      Toast.fail("请求超时，请检查网络");
    } else if (error.message.includes("Network Error")) {
      Toast.fail("网络连接失败，请检查网络");
    } else {
      Toast.fail(error.message || "网络异常");
    }

    return Promise.reject(error);
  },
);

export default request;
